import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'
import { generateClassifiedPropertySampleExcel, parseClassifiedPropertyExcel } from '../../utils/excelTemplate'
import { toast } from 'react-toastify'

export default function AdminAddProperty() {
    const { token } = useAuth()
    const navigate = useNavigate()
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [importing, setImporting] = useState(false)
    const isBrokerFlow = window.location.search.includes('for=broker')
    const [postedBy, setPostedBy] = useState(isBrokerFlow ? 'Broker' : 'Owner')

    const handleDownloadSample = () => {
        generateClassifiedPropertySampleExcel()
    }

    const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const data = await parseClassifiedPropertyExcel(file);
            if (data.length === 0) {
                toast.error("Invalid or empty Excel file");
                setImporting(false);
                return;
            }

            const res = await fetch(`${API_BASE}/classified-properties/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ properties: data, postedBy })
            });

            const result = await res.json();
            if (res.ok) {
                if (result.errorCount > 0) {
                    const failMsg = `Imported ${result.successCount} properties, but ${result.errorCount} failed.`;
                    toast.warning(failMsg);
                    setMsg({ type: 'error', text: `${failMsg} Check console for details.` });
                    console.error("Failed rows details:", result.results.filter((r: any) => !r.success));
                    setTimeout(() => navigate(isBrokerFlow ? '/admin-broker-properties' : '/admin-owner-properties'), 4000);
                } else {
                    toast.success(result.message || "Properties imported successfully!");
                    setMsg({ type: 'success', text: result.message || "Properties imported successfully!" });
                    setTimeout(() => navigate(isBrokerFlow ? '/admin-broker-properties' : '/admin-owner-properties'), 1500);
                }
            } else {
                const failDetails = result.results?.filter((r: any) => !r.success).map((r: any) => r.message).join('; ') || result.message || "Import failed";
                toast.error(`Import failed: ${failDetails}`);
                setMsg({ type: 'error', text: `Import failed: ${failDetails}` });
            }
        } catch (err: any) {
            toast.error(err.message || "An error occurred during import");
            setMsg({ type: 'error', text: err.message || "An error occurred during import" });
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    }

    return (
        <AdminLayout>
            <div className="max-w-4xl mx-auto mt-10 mb-12 select-none">
                <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100 text-center">
                    <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl shadow-sm">
                        <i className="fa-solid fa-file-excel"></i>
                    </div>
                    
                    <h1 className="text-3xl font-extrabold text-[#0f172a] mb-3 leading-none tracking-tight">Import Classified Properties</h1>
                    <p className="text-slate-500 font-medium max-w-md mx-auto mb-10 text-sm">
                        Quickly add multiple properties using our Excel template. Perfect for bulk listings and data migration.
                    </p>



                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                        {/* Download Template */}
                        <button
                            type="button"
                            onClick={handleDownloadSample}
                            className="flex flex-col items-center justify-center p-8 bg-slate-50 hover:bg-slate-100 border-2 border-dashed border-slate-200 rounded-3xl transition-all group"
                        >
                            <div className="w-12 h-12 bg-white text-emerald-600 rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform">
                                <i className="fa-solid fa-cloud-arrow-down text-xl"></i>
                            </div>
                            <span className="text-sm font-bold text-slate-700">Step 1: Download Template</span>
                            <span className="text-xs text-slate-400 mt-1">Get the required format</span>
                        </button>

                        {/* Upload & Import */}
                        <div className="relative group">
                            <div className="flex flex-col items-center justify-center p-8 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-200 rounded-3xl transition-all h-full">
                                <div className="w-12 h-12 bg-white text-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform">
                                    <i className={`fa-solid ${importing ? 'fa-spinner fa-spin' : 'fa-file-import'} text-xl`}></i>
                                </div>
                                <span className="text-sm font-bold text-slate-700">{importing ? 'Processing...' : 'Step 2: Upload & Import'}</span>
                                <span className="text-xs text-slate-400 mt-1">Select your completed Excel file</span>
                                
                                <input
                                    type="file"
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept=".xlsx, .xls"
                                    onChange={handleImportExcel}
                                    disabled={importing}
                                />
                            </div>
                        </div>
                    </div>

                    {msg && (
                        <div className={`mt-8 p-4 rounded-2xl font-bold text-sm max-w-lg mx-auto animate__animated animate__fadeIn ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                            {msg.text}
                        </div>
                    )}
                </div>

                <div className="mt-8 bg-amber-50 border border-amber-100 rounded-2xl p-6">
                    <div className="flex gap-4">
                        <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-circle-info"></i>
                        </div>
                        <div>
                            <h4 className="font-bold text-amber-900 text-sm">Important Instructions</h4>
                            <ul className="mt-2 space-y-1">
                                <li className="text-amber-800/80 text-xs flex items-center gap-2">
                                    <div className="w-1 h-1 bg-amber-400 rounded-full"></div> Do not change the column headers in the template.
                                </li>
                                <li className="text-amber-800/80 text-xs flex items-center gap-2">
                                    <div className="w-1 h-1 bg-amber-400 rounded-full"></div> Ensure all required fields like Price, Area, and City are filled.
                                </li>
                                <li className="text-amber-800/80 text-xs flex items-center gap-2">
                                    <div className="w-1 h-1 bg-amber-400 rounded-full"></div> Max 500 properties per import for optimal performance.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}
